import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NotebookPen, CalendarCheck, Loader2, ChevronLeft, ChevronRight, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const STATUS = ["Prévue", "Réalisée", "Annulée", "Reportée"];
const ATT = [
  { v: "present", l: "Présent", c: "bg-emerald-100 text-emerald-700" },
  { v: "absent", l: "Absent", c: "bg-red-100 text-red-700" },
  { v: "late", l: "Retard", c: "bg-amber-100 text-amber-700" },
  { v: "early_leave", l: "Départ ant.", c: "bg-orange-100 text-orange-700" },
  { v: "unmarked", l: "Non renseigné", c: "bg-slate-100 text-slate-500" },
];

export default function Sessions({ focus = "sessions" }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cahier, setCahier] = useState(null);
  const [appel, setAppel] = useState(null);
  const [students, setStudents] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/pedagogy/sessions?date=${date}`); setRows(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const shift = (n) => { const d = new Date(date); d.setDate(d.getDate() + n); setDate(d.toISOString().slice(0, 10)); };

  const openCahier = (r) => setCahier({ ...r });
  const saveCahier = async () => {
    setBusy(true);
    try {
      await api.post("/pedagogy/sessions/upsert", {
        slot_id: cahier.slot_id, date, title: cahier.title, content_done: cahier.content_done,
        concepts_worked: cahier.concepts_worked, exercises_done: cahier.exercises_done,
        support_used: cahier.support_used, observations: cahier.observations, status: cahier.status,
      });
      toast.success("Cahier de séance enregistré."); setCahier(null); await load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Erreur"); }
    finally { setBusy(false); }
  };

  const openAppel = async (r) => {
    setBusy(true);
    try {
      const { data } = await api.post("/pedagogy/sessions/students", { slot_id: r.slot_id, date });
      setStudents(data.students); setAppel({ ...r, session_id: data.session_id });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Erreur"); }
    finally { setBusy(false); }
  };
  const setAtt = (sid, status) => setStudents((s) => s.map((x) => x.student_id === sid ? { ...x, status } : x));
  const saveAppel = async () => {
    setBusy(true);
    try {
      await api.post(`/pedagogy/sessions/${appel.session_id}/attendance`,
        { records: students.map((s) => ({ student_id: s.student_id, status: s.status, comment: s.comment || "" })) });
      toast.success("Présences enregistrées."); setAppel(null); await load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Erreur"); }
    finally { setBusy(false); }
  };

  const title = focus === "cahier" ? "Cahier de séance" : focus === "attendance" ? "Présences" : "Mes séances";

  return (
    <div data-testid="pedagogy-sessions-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-display font-bold text-slate-900">{title}</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="date-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="session-date" />
          <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="date-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        : rows.length === 0 ? <Card className="p-10 text-center text-slate-400" data-testid="no-sessions">Aucune séance planifiée ce jour.</Card>
          : <div className="grid gap-3">
            {rows.map((r) => (
              <Card key={r.slot_id} data-testid="session-card" className="p-4 flex flex-wrap items-center justify-between gap-3 border-slate-200">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-800">{r.start_time}–{r.end_time}</span>
                    <span className="text-slate-600">{r.group_name || "—"}</span>
                    <Badge variant="secondary">{r.subject_name || "—"}</Badge>
                    <Badge className={r.status === "Réalisée" ? "bg-emerald-100 text-emerald-700 border-0" : "bg-slate-100 text-slate-600 border-0"}>{r.status}</Badge>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    {r.teacher_name || "—"} · {r.cahier_filled ? "Cahier renseigné" : "Cahier à compléter"} · {r.attendance_recorded ? `${r.attendance_count} présences` : "Appel à faire"}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant={focus === "cahier" ? "default" : "outline"} onClick={() => openCahier(r)} data-testid={`cahier-btn-${r.slot_id}`} className="gap-1"><NotebookPen className="h-4 w-4" />Cahier</Button>
                  <Button size="sm" variant={focus === "attendance" ? "default" : "outline"} onClick={() => openAppel(r)} data-testid={`appel-btn-${r.slot_id}`} className="gap-1"><CalendarCheck className="h-4 w-4" />Appel</Button>
                </div>
              </Card>
            ))}
          </div>}

      {/* Cahier dialog */}
      <Dialog open={!!cahier} onOpenChange={(o) => !o && setCahier(null)}>
        <DialogContent className="max-w-lg" data-testid="cahier-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ClipboardList className="h-5 w-5" />Cahier de séance</DialogTitle></DialogHeader>
          {cahier && <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <Input placeholder="Titre de la séance" value={cahier.title || ""} onChange={(e) => setCahier({ ...cahier, title: e.target.value })} data-testid="cahier-title" />
            <Textarea placeholder="Contenu réalisé" rows={3} value={cahier.content_done || ""} onChange={(e) => setCahier({ ...cahier, content_done: e.target.value })} data-testid="cahier-content" />
            <Textarea placeholder="Notions travaillées" rows={2} value={cahier.concepts_worked || ""} onChange={(e) => setCahier({ ...cahier, concepts_worked: e.target.value })} />
            <Textarea placeholder="Exercices faits" rows={2} value={cahier.exercises_done || ""} onChange={(e) => setCahier({ ...cahier, exercises_done: e.target.value })} />
            <Input placeholder="Support utilisé" value={cahier.support_used || ""} onChange={(e) => setCahier({ ...cahier, support_used: e.target.value })} />
            <Textarea placeholder="Observations" rows={2} value={cahier.observations || ""} onChange={(e) => setCahier({ ...cahier, observations: e.target.value })} />
            <Select value={cahier.status || "Prévue"} onValueChange={(v) => setCahier({ ...cahier, status: v })}>
              <SelectTrigger data-testid="cahier-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCahier(null)}>Annuler</Button>
            <Button onClick={saveCahier} disabled={busy} data-testid="cahier-save">{busy ? "…" : "Enregistrer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Appel dialog */}
      <Dialog open={!!appel} onOpenChange={(o) => !o && setAppel(null)}>
        <DialogContent className="max-w-lg" data-testid="appel-dialog">
          <DialogHeader><DialogTitle className="font-display">Appel — {appel?.group_name}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {students.length === 0 ? <p className="text-sm text-slate-400 py-4 text-center">Aucun élève dans ce groupe.</p>
              : students.map((s) => (
                <div key={s.student_id} className="flex items-center justify-between gap-2 border-b pb-2" data-testid="appel-row">
                  <span className="text-sm text-slate-700">{s.name}</span>
                  <div className="flex gap-1">
                    {ATT.map((a) => (
                      <button key={a.v} onClick={() => setAtt(s.student_id, a.v)}
                        className={`text-xs px-2 py-1 rounded-full ${s.status === a.v ? a.c + " font-semibold" : "bg-slate-50 text-slate-400"}`}>
                        {a.l}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAppel(null)}>Annuler</Button>
            <Button onClick={saveAppel} disabled={busy || students.length === 0} data-testid="appel-save">{busy ? "…" : "Enregistrer l'appel"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
